<script>
    import LoginCard from "./components/composites/LoginCard.svelte";
    import HorizontalRule from "./components/presentational/HorizontalRule.svelte";
    import View from "./components/presentational/View.svelte";
    import Header from "./components/Header.svelte";
    import Footer from "./components/Footer.svelte";
    import ContactCard from "./components/composites/ContactCard.svelte";
    import EmailCaptureCard from "./components/composites/EmailCaptureCard.svelte";
    import Premium from "./components/products/Premium.svelte";
    import VPN from "./components/products/VPN.svelte";
    import VPNAlpha from "./components/products/VPNAlpha.svelte";
    import VPNBeta from "./components/products/VPNBeta.svelte";
    import { exchangeDelegationCodeForAuthToken } from "./api/client.js";
    import { authTokenLocalStorage, accountIdLocalStorage } from "./utilities/storage.js";
    import { attemptAuthTokenRefresh, processNewCredentials } from "./utilities/identity.js";
    import { onDestroy, onMount } from "svelte";

    // these stores are initially populated by the LoginCard component at the moment
    import { get, derived } from "svelte/store";
    import { isLoggedIn, authToken, AppData } from "./stores.js";

    import { fetchAccount, logOut, setNewsletterPreference, cancelSubscriptionForProduct } from "./api/client.js";
    import { createEventDispatcher } from "svelte";
    import { t } from "./i18n";

    // Set class on body if touch events are recognized
    // TODO Probably more robust way of doing this
    // Right now it's used to turn off :hover rules.
    if (!("ontouchstart" in document.documentElement)) {
        document.getElementsByTagName("html")[0].classList.add("no-touch");
    }

    const dispatch = createEventDispatcher();

    let isCapturingEmail = false;
    let isLoggingIn = false;

    // this is a "derived store"
    // https://svelte.dev/tutorial/derived-stores
    $: pageProducts = derived(AppData, ($AppData) => {
        const nameToComponent = {
            pid1: VPN,
            pid1alpha: VPNAlpha,
            pid1beta: VPNBeta,
            pid2: Premium,
        };
        return $AppData.catalog.all
            .filter((productKey) => $AppData.catalog.hidden.includes(productKey) === false)
            .filter((productKey) => nameToComponent[productKey])
            .map((productKey) => {
                let subscription = $AppData.subscriptions[productKey];
                if (subscription[0].subscription_data.recurring_frequency.toLowerCase() === "once") {
                    try {
                        const expiration = $AppData.licenses.filter((license) => license.product_id === productKey)[0]["expires"];
                        subscription[0].subscription_data.expires = expiration;
                    } catch (e) {
                        console.log(`Error while adding expires value to subscription info. ${e}`);
                    }
                }
                return {
                    component: nameToComponent[productKey],
                    data: {
                        id: productKey,
                        product: $AppData.products[productKey],
                        subscription
                    },
                };
            });
    });

    const onLogout = () => {
        isLoggedIn.set(false);
        authToken.set("");
        authTokenLocalStorage.remove();
        accountIdLocalStorage.remove();
    };

    // handleLogout is called on logout
    // it destroys the users' auth token and sets app state to logged out
    const handleLogout = () => {
        onLogout();
        logOut({
            onError: (err) => console.log("handle logout error:", err),
        });
    };

    // attemptMagicLogin attempts to log using the auth code in the query parameters
    const attemptMagicLogin = async () =>
        new Promise(async (resolve, _) => {
            try {
                const params = new URLSearchParams(document.location.search.slice(1));
                const authCode = params.get("a"); // ?a=authCode
                if (authCode === null) {
                    resolve(false);
                    return;
                }
                isLoggingIn = true;
                setTimeout(() => {
                    isLoggingIn = false;
                }, 2000);
                console.log("Attempt delegate request with code:", authCode);
                return await exchangeDelegationCodeForAuthToken({
                    code: authCode,
                    onSuccess: ({ data }) => {
                        processNewCredentials(data);
                        resolve(true);
                    },
                    statusHandlers: {
                        "401": () => {
                            console.log("401: exchangeDelegationTokenForAuthToken failed");
                            resolve(false);
                        },
                    },
                });
            } catch (err) {
                console.log("bad query param decode", err);
                resolve(false);
            }
        });

    const getAccount = async () =>
        new Promise((resolve, reject) => {
            fetchAccount({
                onSuccess: ({ data }) => {
                    AppData.set(data);
                    isCapturingEmail = data.primary_email === null;
                    accountIdLocalStorage.set(data.uuid);
                    resolve(true);
                },
                onError: () => {
                    resolve(false);
                },
            });
        });

    // attemptFetchCredentials is called on login. It attempts to fetch preexisting credentials and sets app data if they exist.
    const attemptFetchCredentials = async () => {
        console.log("attempt fetch credentials");
        let gotAccount = false;
        const maybeToken = authTokenLocalStorage.getJSON();
        if (maybeToken) {
            processNewCredentials(maybeToken);
            gotAccount = await getAccount();
        }
        if (gotAccount) {
            console.log("got token from localStorage");
            return true;
        }

        const atrResult = await attemptAuthTokenRefresh();
        gotAccount = await getAccount();
        if (gotAccount) {
            console.log("got token from authTokenRefresh");
            return true;
        }

        await attemptMagicLogin();
        gotAccount = await getAccount();
        if (gotAccount) {
            console.log("got token from magicLogin");
            return true;
        }

        console.log("could not get preexisting token. client must login.");

        // we tried everything
        onLogout();
        return false;
    };

    let attemptedRelogin = false;
    let unsubscribeHooks = [];
    onMount(async () => {
        await attemptFetchCredentials();
        unsubscribeHooks.push(
            isLoggedIn.subscribe(async (isLoggedIn) => {
                console.log("isLoggedIn:", isLoggedIn);
                if (isLoggedIn) {
                    const didGetAccount = await getAccount();
                    if (!didGetAccount) {
                        if (attemptedRelogin) {
                            // Attempt once, trying all credentials sources.
                            // If we attempt more than once per pageload,
                            // it's easy to get into an infinite loop where
                            // we attempt to fetch from sources, attempt to
                            // load page, fail, and as a result, attempt to
                            // fetch from sources... :-X
                            return;
                        }
                        attemptedRelogin = true;
                        await attemptFetchCredentials();
                    }
                }
            })
        );

        unsubscribeHooks.push(
            AppData.subscribe((newData) => {
                if (newData !== null && newData.id !== "") {
                    console.log("got new app data:", newData);
                    isCapturingEmail = newData.primary_email === null;
                }
            })
        );
    });
    onDestroy(() => {
        unsubscribeHooks.forEach((unsubscribe) => unsubscribe());
    });

    const handleNewsLetterToggle = (newsletter) => {
        setNewsletterPreference({
            newsLetterPreference: newsletter,
        }).then((result) => {
            if (result.success) {
                dispatch("message", {
                    action: "STATE_UPDATED",
                });
            }
        });
    };

    const handleCancelSubscription = (subscription) => {
        cancelSubscriptionForProduct(subscription).then((result) => {
            if (result.success) {
                handleMessage({
                    detail: {
                        action: "STATE_UPDATED",
                    },
                });
            }
        });
    };

    const handleMessage = async (msg) => {
        console.log("app got msg", msg);
        switch (msg.detail.action) {
            case "STATE_UPDATED":
                await getAccount();
                break;
            case "UPDATE":
                console.log("newsleter toggle", msg.detail.data);
                handleNewsLetterToggle(msg.detail.data.newsletter);
                break;
            case "CANCEL_SUBSCRIPTION":
                handleCancelSubscription(msg.detail.data);
                break;
            case "LOGOUT":
                handleLogout();
                break;
            default:
                console.log("default app.svelte handlemessage");
                break;
        }
    };
</script>

<svelte:head>
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons|Material+Icons+Outlined" rel="stylesheet" />
    <link href="https://fonts.googleapis.com/css?family=Lato:400,700" rel="stylesheet" />
</svelte:head>

<main>
    <Header isLoggedIn="{$isLoggedIn}" on:message="{handleMessage}" />
    <View>
        {#if $isLoggedIn}
            {#if isCapturingEmail}
                <View viewType="{'page-wrap'}">
                    <EmailCaptureCard data="{$AppData.profile}" on:message="{handleMessage}" />
                </View>
            {:else}
                <View viewType="{'page-wrap'}">
                    <h1>{$t('account_management_page')}</h1>
                    <h2>{$t('products_header')}</h2>
                    <HorizontalRule />
                    {#each $pageProducts as p}
                        <svelte:component this="{p.component}" data="{p.data}" on:message="{handleMessage}" />
                    {/each}
                    <ContactCard data="{$AppData.profile}" isReadOnly=true on:message="{handleMessage}" />
                </View>
            {/if}
        {:else}
            <View viewType="{'page-wrap'}" classNames='{"v-center-h-center"}'>
                <LoginCard isLoggingIn="{isLoggingIn}" />
            </View>
        {/if}
    </View>
    <Footer />
</main>
